import type { TeamJoinRequest } from "~vue/api/generated/team-invitations";
import { ref } from "vue";
import { z } from "zod";

const PENDING_KEY = "nadia.team-join.pending";
const CHECK_KEY = "nadia.team-join.checked";
export const teamJoinUnavailableNotice = ref(false);
const pendingJoinSchema = z.object({
    token: z.string().min(1).max(4096),
    request_id: z.string().uuid(),
    destination: z.enum(["team", "current"]).default("team"),
});
export function teamJoinStorageFailed(): boolean {
    return document.documentElement.dataset.teamJoinStorageFailed === "true";
}

function readPendingTeamJoin(): z.infer<typeof pendingJoinSchema> | null {
    let raw: string | null;
    try {
        raw = globalThis.localStorage.getItem(PENDING_KEY);
    } catch {
        document.documentElement.dataset.teamJoinStorageFailed = "true";
        return null;
    }
    if (!raw) {
        return null;
    }
    try {
        const pending = pendingJoinSchema.safeParse(JSON.parse(raw));
        if (pending.success) {
            return pending.data;
        }
    } catch {
        clearTeamJoin();
        return null;
    }
    clearTeamJoin();
    return null;
}

export function pendingTeamJoin(): TeamJoinRequest | null {
    const pending = readPendingTeamJoin();
    return pending ? { token: pending.token, request_id: pending.request_id } : null;
}

export function teamJoinDestination(): "team" | "current" {
    return readPendingTeamJoin()?.destination ?? "team";
}

export function setTeamJoinDestination(destination: "team" | "current"): void {
    const pending = readPendingTeamJoin();
    if (!pending) {
        return;
    }
    try {
        globalThis.localStorage.setItem(PENDING_KEY, JSON.stringify({ ...pending, destination }));
    } catch {
        document.documentElement.dataset.teamJoinStorageFailed = "true";
    }
}

export function clearTeamJoin(expectedRequestId?: string): void {
    try {
        if (expectedRequestId && pendingTeamJoin()?.request_id !== expectedRequestId) {
            return;
        }
        globalThis.localStorage.removeItem(PENDING_KEY);
    } catch {
        document.documentElement.dataset.teamJoinStorageFailed = "true";
    }
}

export function invitationsChecked(key: string): boolean {
    try {
        return globalThis.sessionStorage.getItem(CHECK_KEY) === key;
    } catch {
        return false;
    }
}

export function markInvitationsChecked(key: string): void {
    try {
        globalThis.sessionStorage.setItem(CHECK_KEY, key);
    } catch {
        // The current mounted coordinator still prevents repeated requests when storage is disabled.
    }
}
